
import { Component } from '@theme/component';
import {
  StandardEvents,
  CartLinesUpdateEvent
} from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

const INSURANCE_VARIANT_ID = 55804637479241;

/**
 * A custom element that manages cart drawer behavior within a <theme-drawer>.
 *
 * Also manages optional delivery protection.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #insuranceUpdating = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.addEventListener(
      ThemeEvents.cartSectionRestored,
      this.#handleCartSectionRestored
    );

    // Delivery protection events
    this.addEventListener(
      'change',
      this.#handleInsuranceChange
    );

    // Synchronize insurance when drawer connects
    this.#syncInsurance();

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    this.removeEventListener(
      ThemeEvents.cartSectionRestored,
      this.#handleCartSectionRestored
    );

    this.removeEventListener(
      'change',
      this.#handleInsuranceChange
    );
  }

  /**
   * Cart section restoration.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => {
      this.#updateStickyState();
      this.#syncInsurance();
    });
  };

  /**
   * Handles drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#syncInsurance();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector(
        'shopify-payment-terms'
      )?.shadowRoot?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Fetch Shopify cart.
   */
  async #getCart() {
    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(`${root}cart.js`, {
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error(
        `Unable to fetch cart: ${response.status}`
      );
    }

    return response.json();
  }

  /**
   * Synchronizes the insurance checkbox with Shopify cart.
   */
  async #syncInsurance() {
    const checkbox = this.querySelector(
      '#pest-delivery-insurance'
    );

    if (!checkbox || this.#insuranceUpdating) return;

    try {
      const cart = await this.#getCart();

      // Make sure the checkbox still exists after the request.
      if (
        !checkbox.isConnected ||
        this.#insuranceUpdating
      ) {
        return;
      }

      const insurance = cart.items.some(
        item => Number(item.variant_id) === INSURANCE_VARIANT_ID
      );

      checkbox.checked = insurance;

    } catch (error) {
      console.error(
        '[cart-drawer] Insurance sync error:',
        error
      );
    }
  }

  /**
   * Handles insurance checkbox changes.
   */
  #handleInsuranceChange = async (event) => {
    const checkbox = event.target;

    if (
      !(checkbox instanceof HTMLInputElement) ||
      checkbox.id !== 'pest-delivery-insurance' ||
      !this.contains(checkbox)
    ) {
      return;
    }

    if (this.#insuranceUpdating) return;

    const selected = checkbox.checked;
    const previousState = !selected;

    this.#insuranceUpdating = true;
    checkbox.disabled = true;

    try {
      const cart = await this.#getCart();

      const insurance = cart.items.find(
        item => Number(item.variant_id) === INSURANCE_VARIANT_ID
      );

      // Add delivery protection
      if (selected && !insurance) {
        await this.#addInsurance();
      }

      // Remove delivery protection
      if (!selected && insurance) {
        await this.#removeInsurance(insurance.key);
      }

      console.info(
        '[cart-drawer] Delivery protection updated:',
        selected
      );

    } catch (error) {
      console.error(
        '[cart-drawer] Insurance update error:',
        error
      );

      // Restore previous checkbox state
      checkbox.checked = previousState;

      alert(
        'Unable to update delivery protection. Please try again.'
      );

    } finally {
      this.#insuranceUpdating = false;

      const currentCheckbox = this.querySelector(
        '#pest-delivery-insurance'
      );

      if (currentCheckbox) {
        currentCheckbox.disabled = false;
      }

      this.#updateStickyState();
    }
  };

  /**
   * Sends a cart mutation through Shopify's native cart event system.
   */
   
  async #mutateInsurance({
    url,
    action,
    body
  }) {
    const root = window.Shopify?.routes?.root || '/';

    const sectionId =
      this.querySelector('cart-items-component')?.dataset.sectionId ||
      'cart-drawer-section';

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    const quantity = action === 'add' ? 1 : 0;

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [
          {
            id: String(INSURANCE_VARIANT_ID),
            quantity
          }
        ],
        promise: deferredUpdatePromise.promise
      })
    );

    try {
      const requestBody = {
        ...body,
        sections: sectionId,
        sections_url: window.location.pathname
      };

      // 1. Add or remove insurance
      const response = await fetch(`${root}${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(
          data.description ||
          data.message ||
          `Shopify cart error: ${response.status}`
        );
      }

      // 2. Fetch the complete updated cart
      const cartResponse = await fetch(`${root}cart.js`, {
        headers: {
          Accept: 'application/json'
        },
        credentials: 'same-origin'
      });

      if (!cartResponse.ok) {
        throw new Error(
          `Unable to fetch updated cart: ${cartResponse.status}`
        );
      }

      const cartData = await cartResponse.json();

      // 3. Resolve the native cart event with the complete cart
      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartData),
        detail: {
          sections: data.sections || undefined,
          items: cartData.items,
          itemCount: Number(cartData.item_count) || 0,
          source: 'delivery-protection',
          didError: false
        }
      });

      return data;

    } catch (error) {
      deferredUpdatePromise.reject(error);
      throw error;
    }
  }


  /**
   * Add delivery protection.
   */
  async #addInsurance() {
    return this.#mutateInsurance({
      url: 'cart/add.js',
      action: 'add',
      body: {
        items: [
          {
            id: INSURANCE_VARIANT_ID,
            quantity: 1
          }
        ]
      }
    });
  }

  /**
   * Remove delivery protection.
   */
  async #removeInsurance(lineKey) {
    return this.#mutateInsurance({
      url: 'cart/change.js',
      action: 'remove',
      body: {
        id: lineKey,
        quantity: 0
      }
    });
  }

  /**
   * Handles native Shopify cart line updates.
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal = (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#syncInsurance();
          });
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Checks if the cart is empty.
   */
  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  /**
   * Recomputes sticky summary state.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
